import "./welcome.css";

/**
 * The welcome window: a modal over the page that offers the ways to open a
 * project. It is shown when the page opens and again from the toolbar's
 * button, so a project can be replaced while the page is open.
 *
 * The items are a tree: a button opens a project, a label holds more items
 * behind a disclosure, and a page replaces the window's content until its
 * back button is pressed. The page builds the tree from what the deployment
 * serves, so a deployment that serves only its own examples offers only
 * those. The tree is asked for each time the window is shown, because what
 * it lists changes while the page is open.
 */
export interface WelcomeWindow {
    /** Open the window at `start`, a page, or at the list without one. */
    show(start?: WelcomePage): void;
    dispose(): void;
}

/** A button, a label with items behind it, or a page. */
export type WelcomeItem = WelcomeEntry | WelcomeChoice | WelcomePage;

export interface WelcomePage {
    /** The button's text and the page's heading. */
    heading: string;
    /** The page's content. `close` closes the window; `list` goes to the list. */
    render(close: () => void, list: () => void): HTMLElement;
}

export interface WelcomeEntry {
    title: string;
    /** Called after the window has closed. */
    open(): void;
}

/**
 * A label, and the items behind it, drawn closed. The items are asked for
 * when the window is shown and listed when they arrive; a rejection is shown
 * in their place.
 */
export interface WelcomeChoice {
    label: string;
    choices: () => Promise<WelcomeItem[]>;
}

export function createWelcome(parent: HTMLElement, items: () => Promise<WelcomeItem[]>): WelcomeWindow {
    const dialog = document.createElement("dialog");
    dialog.className = "idfweb-welcome";
    dialog.setAttribute("aria-labelledby", "idfweb-welcome-title");

    const header = document.createElement("header");
    const back = document.createElement("button");
    back.type = "button";
    back.className = "idfweb-welcome-back";
    back.title = "Back";
    back.setAttribute("aria-label", "Back");
    back.textContent = "‹";
    const title = document.createElement("h1");
    title.id = "idfweb-welcome-title";
    const close = document.createElement("button");
    close.type = "button";
    close.className = "idfweb-welcome-close";
    close.title = "Close";
    close.setAttribute("aria-label", "Close");
    close.textContent = "×";
    close.addEventListener("click", () => dialog.close());
    header.append(back, title, close);
    dialog.append(header);
    parent.append(dialog);

    let content: HTMLElement | undefined;
    const replaceContent = (next: HTMLElement, heading: string, root: boolean) => {
        content?.remove();
        content = next;
        dialog.append(content);
        title.textContent = heading;
        back.hidden = root;
    };
    const showRoot = () => replaceContent(renderList(items(), dialog, showPage), "Open the project", true);
    // A page the window opens at has no back button: it is where the window starts.
    const showPage = (page: WelcomePage, start = false) => {
        replaceContent(
            page.render(() => dialog.close(), showRoot),
            page.heading,
            start,
        );
        content!.querySelector<HTMLElement>("input, button, summary")?.focus();
    };
    back.addEventListener("click", showRoot);

    return {
        show(start?: WelcomePage): void {
            if (dialog.open) return;
            if (start === undefined) showRoot();
            else showPage(start, true);
            dialog.showModal();
        },
        dispose(): void {
            dialog.remove();
        },
    };
}

/** A list of items, filled in when they arrive. */
function renderList(
    items: Promise<WelcomeItem[]>,
    dialog: HTMLDialogElement,
    showPage: (page: WelcomePage) => void,
): HTMLElement {
    const list = document.createElement("div");
    list.className = "idfweb-welcome-list";
    const note = document.createElement("p");
    note.className = "idfweb-welcome-note";
    note.textContent = "Loading…";
    list.append(note);

    items.then(
        (arrived) => {
            note.remove();
            for (const item of arrived) {
                if ("choices" in item) list.append(renderChoice(item, dialog, showPage));
                else if ("render" in item) list.append(renderPageEntry(item, showPage));
                else list.append(renderEntry(item, dialog));
            }
            // Focus the first entry if nothing but the close button has focus.
            const active = document.activeElement;
            if (dialog.open && (active === null || !active.classList.contains("idfweb-welcome-entry"))) {
                dialog.querySelector<HTMLButtonElement>(".idfweb-welcome-entry")?.focus();
            }
        },
        (e: unknown) => {
            note.classList.add("failed");
            note.textContent = e instanceof Error ? e.message : String(e);
        },
    );
    return list;
}

function renderEntry(entry: WelcomeEntry, dialog: HTMLDialogElement): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "idfweb-welcome-entry";
    const title = document.createElement("span");
    title.className = "title";
    title.textContent = entry.title;
    button.append(title);
    button.addEventListener("click", () => {
        dialog.close();
        entry.open();
    });
    return button;
}

function renderPageEntry(page: WelcomePage, showPage: (page: WelcomePage) => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "idfweb-welcome-entry idfweb-welcome-page";
    const title = document.createElement("span");
    title.className = "title";
    title.textContent = page.heading;
    button.append(title);
    button.addEventListener("click", () => showPage(page));
    return button;
}

/**
 * The items behind one label, in a disclosure rather than a `select`: every
 * entry is a button, and a click carries the user activation that opening a
 * directory needs.
 */
function renderChoice(
    choice: WelcomeChoice,
    dialog: HTMLDialogElement,
    showPage: (page: WelcomePage) => void,
): HTMLElement {
    const details = document.createElement("details");
    details.className = "idfweb-welcome-choice";
    const summary = document.createElement("summary");
    summary.className = "idfweb-welcome-entry";
    const label = document.createElement("span");
    label.className = "title";
    label.textContent = choice.label;
    summary.append(label);
    details.append(summary, renderList(choice.choices(), dialog, showPage));
    return details;
}
